import { parseReferenceTokens } from '@worldathletics/utilities';

/** Prefix for internal row-scoped reference chip tokens (`{{ref:ref:<rowId>}}`). */
export const InternalRowTokenPrefix = 'ref:';

/** Prefix for URL row-scoped reference chip tokens (`{{ref:url:<rowId>}}`). */
export const UrlRowTokenPrefix = 'url:';

export type ParsedReferenceTokenId =
  | { kind: 'row-internal'; rowId: string }
  | { kind: 'row-url'; rowId: string }
  | { kind: 'legacy-target'; targetId: string };

export function formatInternalRowToken(rowId: string): string {
  return `${InternalRowTokenPrefix}${rowId}`;
}

export function formatUrlRowToken(rowId: string): string {
  return `${UrlRowTokenPrefix}${rowId}`;
}

export function isRowScopedReferenceId(id: string): boolean {
  return (
    id.startsWith(InternalRowTokenPrefix) || id.startsWith(UrlRowTokenPrefix)
  );
}

export function isInternalRowToken(id: string): boolean {
  return id.startsWith(InternalRowTokenPrefix);
}

export function parseReferenceTokenId(id: string): ParsedReferenceTokenId {
  if (id.startsWith(InternalRowTokenPrefix)) {
    return {
      kind: 'row-internal',
      rowId: id.slice(InternalRowTokenPrefix.length),
    };
  }
  if (id.startsWith(UrlRowTokenPrefix)) {
    return { kind: 'row-url', rowId: id.slice(UrlRowTokenPrefix.length) };
  }
  return { kind: 'legacy-target', targetId: id };
}

export function stripRowTokenPrefix(chipRefId: string): string | null {
  if (chipRefId.startsWith(InternalRowTokenPrefix)) {
    return chipRefId.slice(InternalRowTokenPrefix.length);
  }
  if (chipRefId.startsWith(UrlRowTokenPrefix)) {
    return chipRefId.slice(UrlRowTokenPrefix.length);
  }
  return null;
}

/** Re-export shared helper so existing web imports keep working. */
export { buildSourceScopedRefKey } from '@worldathletics/utilities';

/**
 * Every row id already bound to a row-scoped token (`ref:`/`url:`) ANYWHERE
 * in `text`. Used to exclude rows a sibling occurrence has already claimed
 * when resolving which row a REMAINING legacy chip in the same block means —
 * without this, the block half-migrated by a prior edit (one occurrence
 * rewritten to `ref:<rowId>`, the sibling still legacy) resolves the
 * survivor back onto the already-claimed row and re-creates the cross-talk.
 */
export function collectClaimedRowIds(text: string): Set<string> {
  const ids = new Set<string>();
  for (const segment of parseReferenceTokens(text)) {
    if (segment.type !== 'ref') continue;
    const parsed = parseReferenceTokenId(segment.referenceId);
    if (parsed.kind === 'row-internal' || parsed.kind === 'row-url') {
      ids.add(parsed.rowId);
    }
  }
  return ids;
}
